import { Component, OnInit, Input, ViewChild, ChangeDetectorRef } from '@angular/core';

import * as uuid from 'uuid';
import 'bootstrap';

import { ModalControl } from '../modal-control/modal-control.component';
import { UploadExcelOption, Validation, ConfigResult } from './upload-excel-control.model';
import { ExcelService } from '../../services/excel/excel.service';
import { UtilityService } from '../../services/utility/utility.service';
import { LoadingService } from '../../services/loading/loading.service';
import { UploadExcelConfigComponent } from './dependencies/upload-excel-config/upload-excel-config.component';
import { ExcelTemplate } from '../../services/excel/excel.model';
import { NotifyService } from '../../services/notify/notify.service';
import { TranslateService } from '../../services/translate/translate.service';

@Component({
  selector: 'sd-upload-excel-control',
  templateUrl: './upload-excel-control.component.html',
  styleUrls: [
    '../../assets/css/button.scss',
    '../../assets/css/pagination.scss',
    '../../assets/css/scrollbar.scss',
    '../../assets/css/box.scss',
    './upload-excel-control.component.css'
  ]
})
export class UploadExcelControlComponent implements OnInit {
  @Input() option: UploadExcelOption;
  @ViewChild('modal') modal: ModalControl;
  @ViewChild('uploadExcelConfig', { static: true }) uploadExcelConfig: UploadExcelConfigComponent;
  configResult: ConfigResult;
  items: {
    validation: Validation,
    [column: string]: any
  }[] = [];
  hasDescription = false;
  // Paginate
  pages = [1];
  pageSize = 50;
  currentPage = 1;
  pageCount = 5;
  totalPage = 1;

  showing: 'ALL' | 'SUCCESS' | 'WARNING' | 'ERROR' = 'ALL';
  filteredItems = [];
  viewItems = [];
  numberOfSuccess = 0;
  numberOfError = 0;
  numberOfWarning = 0;
  file: File;
  constructor(
    private ref: ChangeDetectorRef,
    private excelService: ExcelService,
    private translateService: TranslateService,
    private notifyService: NotifyService,
    private utilityService: UtilityService,
    private loadingService: LoadingService) {
  }

  ngOnInit() {
    this.uploadExcelConfig.setOption({
      key: this.option.key,
      columns: this.option.columns.filter(e => !e.hidden)
    });
  }

  async loadConfig(configResult: ConfigResult) {
    this.configResult = configResult;
    this.option.columns = configResult.columns;
  }

  open() {
    this.items = [];
    this.numberOfSuccess = 0;
    this.numberOfError = 0;
    this.numberOfWarning = 0;
    this.modal.open();
  }

  close() {
    this.modal.close();
  }

  reload() {
    this.currentPage = this.currentPage || 1;
    this.filteredItems = this.items.filter((item) => {
      if (this.showing === 'SUCCESS') {
        return !item.validation.error && !item.validation.warning;
      } else if (this.showing === 'WARNING') {
        return !!item.validation.warning && !item.validation.error;
      } else if (this.showing === 'ERROR') {
        return !!item.validation.error;
      }
      return true;
    });
    this.viewItems = this.filteredItems
      .filter((item, index) => index >= (this.currentPage - 1) * this.pageSize && index < this.currentPage * this.pageSize);

    this.numberOfSuccess = this.items.filter(item => !item.validation.error && !item.validation.warning).length;
    this.numberOfError = this.items.filter(item => item.validation.error).length;
    this.numberOfWarning = this.items.filter(item => item.validation.warning && !item.validation.error).length;
  }

  upload = async () => {
    try {
      const { translate } = this.translateService;
      const { items, file } = await this.excelService.import();
      if (this.option.columns.some(e => !!e.description)) {
        this.hasDescription = true;
      } else {
        this.hasDescription = false;
      }
      const offset = this.hasDescription ? 2 : 1;
      items.splice(0, offset);
      if (items.length === 0) {
        this.notifyService.notify.warning(`${translate(`No data to import`)}`);
        return;
      }
      if (items.length > this.option.limit) {
        this.notifyService.notify.warning(`${translate(`Limit rows`)}: ${this.option.limit}`);
        return;
      }
      this.items = this.option.mappingItems?.(items) || items;
      this.filteredItems = this.items;
      this.showing = 'ALL';
      this.file = file;
      this.currentPage = 1;
      this.generatePaginate();

      for (const [idx, item] of this.items.entries()) {
        item.lineId = uuid.v4();
        item.excelIndex = idx + (this.hasDescription ? 4 : 3);
        await this.validate(item);
      }

      // Nếu không có error thì tiếp tục validate từ option validate truyền vào
      if (!this.items.some(item => !!item.validation.error) && this.option.errorValidation) {
        this.loadingService.start();
        const results = await this.option.errorValidation(this.items);
        results.forEach(result => this.items[result.idx].validation.error = result.message);
        this.loadingService.stop();
      }

      // Nếu không có warning thì tiếp tục validate từ option validate truyền vào
      if (!this.items.some(item => !!item.validation.warning) && this.option.warningValidation) {
        this.loadingService.start();
        const results = await this.option.warningValidation(this.items);
        results.forEach(result => this.items[result.idx].validation.warning = result.message);
        this.loadingService.stop();
      }

      this.reload();
      this.ref.detectChanges();
    } catch (err) {
      this.utilityService.handle.error(err);
    }
  }

  async validate(item: { [x: string]: any; validation: Validation; }) {
    const validation: Validation = {
      error: '',
      warning: '',
      data: {}
    };
    const { translate } = this.translateService;
    for (const column of this.option.columns) {
      validation.data[column.field] = {};
      try {
        if (column.defaultValue !== undefined) {
          item[column.field] = item[column.field] ?? column.defaultValue;
        } else if (column.required && !item[column.field] && item[column.field] !== 0) {
          validation.data[column.field].error = `${translate('required')}`;
          validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
          continue;
        }
        if (column.type === 'string') {
          const value = (item[column.field] || '').toString();
          if (column.minLength !== undefined && value.length < column.minLength) {
            validation.data[column.field].error = `${translate('Min length')}: ${column.minLength}`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
          if (column.maxLength !== undefined && value.length > column.maxLength) {
            validation.data[column.field].error = `${translate('Max length')}: ${column.maxLength}`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
        } else if (column.type === 'number') {
          if (item[column.field]) {
            const value = +item[column.field];
            if (Number.isNaN(value)) {
              validation.data[column.field].error = `${item[column.field]} ${translate('is not a number')}`;
              validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
              continue;
            }
            item[column.field] = value;
          }
          if (!item[column.field] && item[column.field] !== 0) {
            item[column.field] = undefined;
          }
          if (column.minValue !== undefined && item[column.field] < column.minValue) {
            validation.data[column.field].error = `${item[column.field]} ${translate('Min value')}: ${column.minValue}`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
          if (column.maxValue !== undefined && item[column.field] > column.maxValue) {
            validation.data[column.field].error = `${item[column.field]} ${translate('Max value')}: ${column.maxValue}`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
        } else if (column.type === 'bool') {
          if (item[column.field] === 0 || item[column.field] === false) {
            item[column.field] = '0';
          }
          if (item[column.field] === 1 || item[column.field] === true) {
            item[column.field] = '1';
          }
          if (item[column.field]) {
            const value = +item[column.field];
            if (Number.isNaN(value) || (value !== 0 && value !== 1)) {
              validation.data[column.field].error = `${translate('Value shoud be 0 or 1')}`;
              validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            }
          }
        } else if (column.type === 'values') {
          if (typeof (item[column.field]) !== 'number' && typeof (item[column.field]) !== 'string') {
            validation.data[column.field].error = `${item[column.field]} ${translate('Invalid data type')}`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
          if (column.checkValueInArray && item[column.field] && !column.values.some(e => e.toString() === item[column.field].toString())) {
            validation.data[column.field].error = `${item[column.field]} ${translate('Value not in')} [${column.values.join()}]`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
        } else if (column.type === 'radio') {
          if (typeof (item[column.field]) !== 'number' && typeof (item[column.field]) !== 'string') {
            validation.data[column.field].error = `${item[column.field]} ${translate('Invalid data type')}`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
          if (column.checkValueInArray && item[column.field] && !column.values.some(e => e.toString() === item[column.field].toString())) {
            validation.data[column.field].error = `${item[column.field]} ${translate('Value not in')} [${column.values.join()}]`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
        } else if (column.type === 'date') {
          if (column.format && item[column.field]) {
            if (!Date.isDate(Date.parseFrom(item[column.field], column.format))) {
              validation.data[column.field].error = `${translate('Invalid format')} ${column.format}: ${item[column.field]}`;
              validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            }
          }

          if (item[column.field] && !Date.isDate(item[column.field])) {
            validation.data[column.field].error = `${translate('Invalid date')}: ${item[column.field]}`;
            validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
            continue;
          }
        }

        if (!validation.data[column.field].error && column.errorValidation) {
          const error = await column.errorValidation(item, item[column.field]);
          if (error) {
            validation.data[column.field].error = `${error}`;
            validation.error += `[${column.field}] ${error}\n`;
          }
        }

        if (!validation.data[column.field].warning && column.warningValidation) {
          const warning = await column.warningValidation(item, item[column.field]);
          if (warning) {
            validation.data[column.field].warning = `${warning}`;
            validation.warning += `[${column.field}] ${warning}\n`;
          }
        }
      } catch (ex) {
        validation.data[column.field].error = `${ex?.message || ex?.error || ex || 'Có lỗi xảy ra'}`;
        validation.error += `[${column.field}] ${validation.data[column.field].error}\n`;
      }
    }
    item.validation = validation;
  }

  private generatePaginate() {
    this.pages = [];
    this.totalPage = Math.trunc(this.filteredItems.length / this.pageSize) + (this.filteredItems.length % this.pageSize === 0 ? 0 : 1);
    if (this.currentPage > this.totalPage) {
      this.currentPage = this.totalPage;
    }
    if (this.totalPage <= this.pageCount) {
      for (let i = 1; i <= this.totalPage; i++) {
        this.pages.push(i);
      }
    } else {
      if (this.currentPage < 3) {
        this.pages = [1, 2, 3, 4, 5];
      } else if (this.currentPage > this.totalPage - 2) {
        this.pages = [this.totalPage - 4, this.totalPage - 3, this.totalPage - 2, this.totalPage - 1, this.totalPage];
      } else {
        this.pages = [this.currentPage - 2, this.currentPage - 1, this.currentPage, this.currentPage + 1, this.currentPage + 2];
      }
    }
  }

  selectPage(selectedPage: number) {
    const page = +selectedPage;
    if (this.currentPage !== page && page > 0 && page <= this.totalPage) {
      this.currentPage = page;
    }
    this.reload();
    this.generatePaginate();
  }

  accept() {
    const successItems = this.items.filter(item => !item.validation.error && !item.validation.warning);
    const errorItems = this.items.filter(item => item.validation.error);
    const warningItems = this.items.filter(item => !item.validation.error && item.validation.warning);
    this.option?.onAccept(this.items, successItems, errorItems, warningItems, this.file);
  }

  setValidation = (validates: { idx: number, message: string }[]) => {
    if (Array.isArray(validates)) {
      validates.forEach(validate => this.items[validate.idx].validation.error = validate.message);
    }
    this.reload();
    this.ref.detectChanges();
  }

  view = (showing: 'ALL' | 'SUCCESS' | 'WARNING' | 'ERROR' = 'ALL') => {
    this.showing = showing;
    this.currentPage = 1;
    this.reload();
    this.generatePaginate();
  }

  downloadTemplate = async () => {
    const sheets: {
      name: string,
      items: any[],
      fields: string[]
    }[] = [];
    if (Array.isArray(this.option?.sheets)) {
      for (const sheet of this.option.sheets) {
        if (sheet.name && sheet.items && sheet.fields) {
          if (Array.isArray(sheet.items)) {
            sheets.push({
              name: sheet.name,
              items: sheet.items,
              fields: sheet.fields
            });
          } else {
            sheets.push({
              name: sheet.name,
              items: await sheet.items(),
              fields: sheet.fields
            });
          }
        }
      }
    }
    const excelTemplate: ExcelTemplate = {
      fileName: this.option.fileName || 'Template',
      columns: this.option.columns.map(column => {
        return {
          field: column.field,
          title: column.title,
          width: column.width,
          required: column.required,
          description: column.description
        };
      }),
      sheets
    };
    const result = await this.excelService.generateExcelTemplate(excelTemplate);
    if (result?.filePath) {
      await this.excelService.downloadTemplate(result.filePath, excelTemplate.fileName || 'Template.xlsx');
    }
  }
}
